import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  IconButton, 
  Avatar, 
  Chip, 
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import BookMyOpAPI from '../services/BookMyOpapi';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

const CommunicationDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [actionError, setActionError] = useState(null);
  
  useEffect(() => {
    fetchTicket();
  }, [id]);
  
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket]);
  
  const fetchTicket = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await BookMyOpAPI.getCommunicationById(id);
      setTicket(response);
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError(err.message || 'Failed to load ticket details. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch replies separately if needed
  const fetchReplies = async () => {
    if (!id) return;
    try {
      const replies = await BookMyOpAPI.getReplies(id);
      // Update ticket with replies if they're not already included
      if (replies && replies.length > 0) {
        setTicket(prev => prev ? { ...prev, replies: replies } : null);
      }
    } catch (err) {
      console.error('Error fetching replies:', err);
    }
  };
  
  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    
    setSending(true);
    setSendError(null);
    try {
      const data = {
        message: replyMessage,
        attachment: attachment
      };
      
      await BookMyOpAPI.replyCommunication(id, data);
      setReplyMessage('');
      setAttachment(null);
      setActionMessage('Reply sent successfully');
      setTimeout(() => setActionMessage(null), 3000);
      
      setTimeout(() => {
        fetchTicket();
      }, 300);
    } catch (err) {
      setSendError(err.message || 'Failed to send reply. Please try again.');
    } finally {
      setSending(false);
    }
  };
  
  const handleCloseTicket = async () => {
    try {
      setActionMessage(null);
      setActionError(null);
      
      await BookMyOpAPI.closeCommunication(id);
      setActionMessage('Ticket closed successfully');
      setTimeout(() => setActionMessage(null), 3000);
      
      setTimeout(() => {
        fetchTicket();
      }, 300);
    } catch (err) {
      setActionError('Failed to close ticket. Please try again.');
      setTimeout(() => setActionError(null), 5000);
    }
  };
  
  const handleReopenTicket = async () => {
    try {
      setActionMessage(null);
      setActionError(null);
      
      await BookMyOpAPI.reopenCommunication(id);
      setActionMessage('Ticket reopened successfully');
      setTimeout(() => setActionMessage(null), 3000);
      
      setTimeout(() => {
        fetchTicket();
      }, 300);
    } catch (err) {
      setActionError('Failed to reopen ticket. Please try again.');
      setTimeout(() => setActionError(null), 5000);
    }
  };
  
  const handleFileChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      setAttachment(event.target.files[0]);
    }
  };
  
  const handleRemoveAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const getMessages = () => {
    if (!ticket) return [];
    
    const messages = [{
      id: ticket.id,
      sender_type: ticket.sender_type,
      sender_name: ticket.sender_details?.name || 'Unknown',
      message: ticket.message,
      created_at: ticket.created_at,
      attachment: ticket.attachment
    }];
    
    if (ticket.replies && Array.isArray(ticket.replies)) {
      messages.push(...ticket.replies.map(reply => ({
        ...reply,
        sender_type: reply.sender_type,
        sender_name: reply.sender_details?.name || 'Unknown',
        attachment: reply.attachment
      })));
    }
    
    messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return messages;
  };
  
  const renderMessageBubble = (message) => {
    const isPatient = message.sender_type === 'bookmyop_patient';
    
    const getInitials = (name) => {
      if (!name) return '?';
      return name.split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    };
    
    const senderName = message.sender_name || message.sender_details?.name || 'Unknown';
    const senderInitials = getInitials(senderName);
    
    return (
      <Box
        key={message.id}
        sx={{
          display: 'flex',
          justifyContent: isPatient ? 'flex-start' : 'flex-end',
          mb: 4
        }}
      >
        {isPatient && (
          <Avatar
            sx={{ 
              bgcolor: 'primary.main',
              mr: 1
            }}
          >
            {senderInitials}
          </Avatar>
        )}
        
        <Box sx={{ maxWidth: '80%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 'bold',
                color: isPatient ? 'primary.main' : 'secondary.main'
              }}
            >
              {senderName}
            </Typography>
          </Box>
          
          <Paper 
            elevation={1}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isPatient ? 'background.paper' : 'primary.light',
              color: isPatient ? 'text.primary' : 'primary.contrastText'
            }}
          >
            <Typography variant="body1">{message.message}</Typography>
            
            {message.attachment && (
              <Box mt={2}>
                <Button
                  startIcon={<FileIcon />}
                  variant="outlined"
                  size="small"
                  component="a"
                  href={message.attachment}
                  target="_blank"
                  download
                  sx={{
                    color: isPatient ? 'primary.main' : 'inherit',
                    borderColor: isPatient ? 'primary.main' : 'inherit',
                  }}
                >
                  View Attachment
                </Button>
              </Box>
            )}
          </Paper>
          
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              display: 'block', 
              mt: 0.5,
              textAlign: isPatient ? 'left' : 'right' 
            }}
          >
            {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
          </Typography>
        </Box>
        
        {!isPatient && (
          <Avatar
            sx={{ 
              bgcolor: 'secondary.main',
              ml: 1
            }}
          >
            {senderInitials}
          </Avatar>
        )}
      </Box>
    );
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box mt={3}>
        <Alert severity="error">{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/support')}
          sx={{ mt: 2 }}
        >
          Back to Tickets
        </Button>
      </Box>
    );
  }
  
  if (!ticket) {
    return (
      <Box mt={3}>
        <Alert severity="warning">Ticket not found</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/support')}
          sx={{ mt: 2 }}
        >
          Back to Tickets
        </Button>
      </Box>
    );
  }
  
  const messages = getMessages();
  const isOpen = ticket.status?.toLowerCase() === 'open';
  
  return (
    <Box sx={{ pb: { xs: '124px', md: '32px' },px: { xs: '16px', md: '32px' ,lg:6},py:{xs:2,md:4,lg:6},bgcolor:`${theme.palette.background.default} !important` }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}  >
        <Box display="flex" alignItems="center" >
          <IconButton onClick={() => navigate('/support')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h4" fontWeight={600}>
            Ticket #{id}
          </Typography>
          <Chip 
            label={ticket.status_display || ticket.status} 
            color={
              ticket.status?.toLowerCase() === 'open' ? 'success' :
              ticket.status?.toLowerCase() === 'closed' ? 'default' :
              ticket.status?.toLowerCase() === 'resolved' ? 'primary' : 'default'
            }
            sx={{ ml: 2 }} 
          />
        </Box>
        
      </Box>
        <Box sx={{mb:2}}>
          {isOpen ? (
            ticket.is_sender && (
              <Tooltip title="Close this ticket">
                <Button 
                  variant="outlined" 
                  color="primary" 
                  size="small"
                  fullWidth
                  startIcon={<CheckIcon />}
                  onClick={handleCloseTicket}
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    px: 2
                  }}
                >
                  Close Ticket
                </Button>
              </Tooltip>
            )
          ) : (
            <Tooltip title="Reopen this ticket">
              <Button 
                size="small"
                fullWidth
                startIcon={<RefreshIcon />}
                variant="outlined" 
                color="primary" 
                
                onClick={handleReopenTicket}
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  px: 2
                }}
              >
                Reopen Ticket
              </Button>
            </Tooltip>
          )}
        </Box>
      
      {/* Success or Error Messages */}
      {actionMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {actionMessage}
        </Alert>
      )}
      
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}
      
      {/* Main Content - Side by Side on Desktop */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        mb: 3
      }}>
        {/* Ticket Information */}
        <Card sx={{ 
          mb: { xs: 0, md: 0 },
          width: { xs: '100%', md: '350px' },
          flexShrink: 0,
          // maxHeight: { md: '60vh' },
          overflow: { md: 'auto' }
        }}>
          <CardContent>
            <Box >
              <Typography variant="overline" color="text.secondary">
                SUBJECT
              </Typography>
              <Typography variant="body1" gutterBottom>
                {ticket.subject}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                STATUS
              </Typography>
              <Typography variant="body1" gutterBottom>
                <Chip 
                  label={ticket.status_display || ticket.status} 
                  color={
                    ticket.status?.toLowerCase() === 'open' ? 'success' :
                    ticket.status?.toLowerCase() === 'closed' ? 'default' :
                    ticket.status?.toLowerCase() === 'resolved' ? 'primary' : 'default'
                  }
                  size="small"
                />
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                SENDER
              </Typography>
              <Typography variant="body1" gutterBottom>
                {ticket.sender_name || ticket.sender_details?.name || 'Unknown'}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                RECEIVER
              </Typography>
              <Typography variant="body1" gutterBottom>
                {ticket.receiver_name || ticket.receiver_details?.name || ticket.receiver_details?.email || 'Unknown'}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                CREATED
              </Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(ticket.created_at).toLocaleString()}
              </Typography>
            </Box>

            {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  LAST UPDATED
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date(ticket.updated_at).toLocaleString()}
                </Typography>
              </Box>
            )}

            {ticket.message_count > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  MESSAGE COUNT
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {ticket.message_count}
                </Typography>
              </Box>
            )}

            {ticket.closed_at && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  CLOSED AT
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date(ticket.closed_at).toLocaleString()}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
        
        {/* Message Thread */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            Conversation
          </Typography>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3,
              flex: 1,
              minHeight: '400px',
              maxHeight: { xs: '50vh', md: 'calc(60vh - 40px)' },
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {messages.map(renderMessageBubble)}
              <div ref={messagesEndRef} />
            </Box>
          </Paper>
        </Box>
      </Box>
      
      {/* Reply Form */}
      {isOpen ? (
        <Box>
          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sendError}
            </Alert>
          )}
          
          {attachment && (
            <Paper sx={{ p: 1, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box display="flex" alignItems="center">
                <FileIcon sx={{ mr: 1 }} />
                <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                  {attachment.name}
                </Typography>
              </Box>
              <IconButton size="small" onClick={handleRemoveAttachment}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          )}
          
          <Box display="flex" alignItems="flex-end">
            <TextField
              fullWidth
              placeholder="Type your reply..."
              variant="outlined"
              multiline
              maxRows={4}
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              disabled={sending}
            />
            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <Tooltip title="Attach a file">
              <IconButton 
                color="primary" 
                sx={{ ml: 1 }}
                onClick={() => fileInputRef.current.click()}
                disabled={sending || attachment !== null}
              >
                <AttachFileIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              disabled={!replyMessage.trim() || sending}
              onClick={handleSendReply}
              sx={{ ml: 1 }}
            >
              {sending ? <CircularProgress size={24} /> : 'Send'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Alert severity="info" icon={<CloseIcon />}>
          This ticket is closed. You cannot reply to it anymore.
        </Alert>
      )}
    </Box>
  );
};

export default CommunicationDetail; 